import React from 'react'
import PropTypes from 'prop-types'
import { StaticQuery, graphql } from 'gatsby'
import styled from 'styled-components'
import { Spring } from 'react-spring/renderprops'
import { Helmet } from 'react-helmet'
import Header from './Header'
import Filters from './Filters'
import WithFocus from './WithFocus'

import App from '../containers/App'

import colors from '../styles/colors'
import 'react-datepicker/dist/react-datepicker.css'
import { mainContentTagId } from '../utils/constants'
import SkipNav from './SkipNav/SkipNav'

const FILTERS_WIDTH = 330

const Layout = ({ children, bgGrey = false, ...props }) => (
  <StaticQuery
    query={graphql`
      query SiteTitleQuery {
        site {
          siteMetadata {
            title
          }
        }
      }
    `}
    render={(data) => (
      <>
        <Helmet htmlAttributes={{ lang: 'pl-PL' }}>
          <title>Kompleksowa Analiza Procesów Poznawczych</title>
        </Helmet>
        <App.Consumer>
          {(context) => {
            const isFilterActive = props.filters && context.filtersActive
            const width = isFilterActive ? FILTERS_WIDTH : 0
            return (
              <Spring to={{ width }}>
                {(style) => (
                  <Wrapper sidebarWidth={style.width}>
                    <SkipNav idTagToSkip={mainContentTagId} />
                    <StyledPageContent bgGrey={bgGrey}>
                      <Header
                        siteTitle={data.site.siteMetadata.title}
                        sidebarWidth={style.width}
                        logout={
                          props.modalLogout ? props.modalLogout : context.logout
                        }
                      />
                      <main id={mainContentTagId}>{children}</main>
                    </StyledPageContent>
                    {props.filters && isFilterActive && (
                      <WithFocus isActive={isFilterActive}>
                        <Filters style={style}>{props.filters}</Filters>
                      </WithFocus>
                    )}
                  </Wrapper>
                )}
              </Spring>
            )
          }}
        </App.Consumer>
      </>
    )}
  />
)

const StyledPageContent = styled.div`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  flex: 1 1;
  position: relative;
  height: 100vh;
  overflow: auto;
  padding-top: 70px;
  > div {
    padding: 0 20px;
  }
`

const Wrapper = styled.div.attrs({
  style: (props) => ({
    paddingRight: `${props.sidebarWidth}px`,
  }),
})`
  display: grid;
  background: #ddd;

  position: relative;
  grid: 70px 1fr /1fr;
  .react-datepicker {
    border-radius: 5px;
    background-color: ${colors.white};
    border: 1px solid ${colors.gray};
    padding: 5px;
    font-family: Raleway;
    display: flex;
    flex-direction: row;
  }

  .react-datepicker-popper {
    left: 10px !important;
    z-index: 6;
  }

  .react-datepicker__triangle {
    display: none;
  }

  .react-datepicker__navigation {
    top: 20px;
    width: 0;
  }

  .react-datepicker__navigation--previous:hover {
    border-right-color: #415261;
  }

  .react-datepicker__navigation--next:hover {
    border-left-color: #415261;
  }

  .react-datepicker__navigation--previous:before,
  .react-datepicker__navigation--next:before {
    content: '';
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
    height: 36px;
    width: 36px;
    border: 1px solid rgba(175, 185, 192, 0.4);
    background-color: transparent;
    z-index: -1;
  }

  .react-datepicker__navigation--previous:before {
    border-radius: 4px 0 0 4px;
    left: -13px;
    top: -17px;
  }

  .react-datepicker__navigation--next:before {
    border-radius: 0 4px 4px 0;
    left: -22px;
    top: -17px;
  }

  .react-datepicker__navigation--previous {
    left: 31px;
  }

  .react-datepicker__navigation--next {
    right: 31px;
  }

  .react-datepicker__navigation--years-previous {
    top: 10px !important;
  }

  .react-datepicker__navigation--years-upcoming {
    top: -10px !important;
  }

  .react-datepicker__day {
    color: ${colors.black};
    font-size: 13px;
    font-weight: 500;
    line-height: 38px;
    text-align: center;
    width: 40px;
    height: 40px;
    border-radius: 100%;
  }

  .react-datepicker__day:hover {
    border-radius: 100%;
    background-color: rgba(175, 185, 192, 0.6);
    color: ${colors.monza};
  }

  .react-datepicker__day--disabled {
    color: ${colors.gray} !important;
  }

  .react-datepicker__day--disabled:hover {
    background-color: transparent;
    color: ${colors.gray};
  }

  .react-datepicker__month {
    background-color: transparent;
    margin: 0;
  }

  .react-datepicker__month-container {
    width: 100%;
  }

  .react-datepicker__current-month {
    color: ${colors.black};
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 1px;
    line-height: 16px;
    text-transform: uppercase;
    padding-top: 5px;
    display: none;
  }

  .react-datepicker__month-dropdown {
    background-color: ${colors.white};
    border-radius: 0;
    border: 1px solid ${colors.gray};
    left: 15%;
    top: 10px;
  }

  .react-datepicker__month-option {
    &:hover {
      background-color: ${colors.gray};
    }

    &:first-of-type,
    &:last-of-type {
      border-radius: 0;
    }
  }

  .react-datepicker__day--keyboard-selected,
  .react-datepicker__day--keyboard-selected:hover,
  .react-datepicker__day--selected,
  .react-datepicker__day--selected:hover,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--in-range {
    border-radius: 100%;
    background-color: ${colors.monza};
    color: #fff;
  }

  .react-datepicker__day-names {
    display: flex;
    justify-content: space-between;
    flex-direction: row;
  }

  .react-datepicker__day-name {
    text-align: center;
    width: 40px;
    height: 40px;
    color: ${colors.black};
    font-weight: 600;
    text-transform: uppercase;
  }

  .react-datepicker__week {
    display: flex;
    justify-content: space-between;
  }

  .react-datepicker__day--weekend {
    color: ${colors.monza};
    &.react-datepicker__day--selected {
      color: ${colors.white};
    }
  }

  .react-datepicker__day--outside-month {
    color: ${colors.gray} !important;
  }

  .react-datepicker-wrapper input {
  }

  .react-datepicker__input-container input {
  }
  .react-datepicker__input-container input:focus {
    outline: none;
  }

  .react-datepicker-wrapper input:focus {
    outline: none;
  }

  .react-datepicker__header {
    background-color: #fff;
    border-bottom: none;
    padding-top: 0;
  }

  .react-datepicker__year-select {
    font-size: 11px;
  }

  .react-datepicker__year-dropdown {
    background-color: ${colors.white};
    border-radius: 0;
    border-color: ${colors.gray};
    box-shadow: none;
    top: 10px;
    left: 40%;
  }

  .react-datepicker__year-option {
    &:hover {
      background-color: ${colors.gray};
    }

    &:first-of-type,
    &:last-of-type {
      border-radius: 0;
    }
  }

  .react-datepicker__header__dropdown {
    height: 25px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px 0 10px;
    color: ${colors.black};
    text-transform: uppercase;
  }

  .react-datepicker__year-read-view--down-arrow,
  .react-datepicker__month-read-view--down-arrow {
    top: 3px;
    margin-left: 30px;
  }

  .react-datepicker__month-read-view--selected-month,
  .react-datepicker__year-read-view--selected-year {
    font-weight: 600;
  }
`

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
